
"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Time } from "@internationalized/date"
import * as PopoverPrimitives from "@radix-ui/react-popover"
import {
    type TimeValue,
} from "@react-aria/datepicker"
import { RiSubtractFill } from "@remixicon/react"

import { isSameDay } from "date-fns"
import { enUS } from "date-fns/locale"
import { cx } from "@/lib/utils"
import { Button } from "@/components/atoms/Button"
import { Checkbox } from "@/components/atoms/Checkbox"
import { Calendar as CalendarPrimitive, } from "@/components/molecules/Calendar"
import { DateRangePreset, DateRange } from "./Preset"
import { PickerProps } from "./DatePicker"
import { CalendarPopover } from "./CalendarPopover"
import { Trigger } from "./Trigger"
import { TimeInput } from "./TimeInput"
import { PresetContainer } from "./Preset"
import { formatDate, getCompareRange, getQuickSelectRange, type CompareMode, type QuickSelectKey } from "./utils"

type ShowCompareOptions = {
    [key in CompareMode]?: boolean
}

export type RangeDatePickerProps = {
    presets?: DateRangePreset[]
    defaultValue?: DateRange
    value?: DateRange
    onChange?: (dateRange: DateRange | undefined) => void
    showQuickSelect?: boolean
    showCompare?: boolean
    compare?: boolean
    showCompareOptions?: ShowCompareOptions
    onCompareChange?: (checked: boolean) => void
    compareMode?: CompareMode
    onCompareModeChange?: (mode: CompareMode | undefined) => void
    compareRange?: DateRange
    onCompareRangeChange?: (dateRange: DateRange | undefined) => void
    trigger?: React.ReactNode
} & PickerProps

interface RangeProps extends PickerProps {
    presets?: DateRangePreset[]
    defaultValue?: DateRange
    value?: DateRange
    onChange?: (dateRange: DateRange | undefined) => void
    showQuickSelect?: boolean
    showCompare?: boolean
    showCompareOptions?: ShowCompareOptions
    compare?: boolean
    onCompareChange?: (checked: boolean) => void
    compareMode?: CompareMode
    onCompareModeChange?: (mode: CompareMode | undefined) => void
    compareRange?: DateRange
    onCompareRangeChange?: (dateRange: DateRange | undefined) => void
    trigger?: React.ReactNode
}

const RangeDatePicker = ({
    defaultValue,
    value,
    onChange,
    presets,
    showQuickSelect,
    showCompare,
    showCompareOptions,
    compare,
    onCompareChange,
    compareMode,
    onCompareModeChange,
    compareRange,
    onCompareRangeChange,
    disabled,
    disableNavigation,
    disabledDays,
    enableYearNavigation = false,
    locale = enUS,
    showTimePicker,
    placeholder = "Select date range",
    hasError,
    translations,
    align = "center",
    className,
    trigger,
    ...props
}: RangeProps) => {
    const [open, setOpen] = useState(false)
    const [range, setRange] = useState<DateRange | undefined>(
        value ?? defaultValue ?? undefined,
    )
    const monthStart = (date: Date, offsetMonths = 0) => {
        return new Date(date.getFullYear(), date.getMonth() + offsetMonths, 1)
    }

    const [leftMonth, setLeftMonth] = useState<Date | undefined>(
        range?.from ? monthStart(range.from) : undefined,
    )

    const [compareInternal, setCompareInternal] = useState<boolean>(compare ?? false)

    const [compareModeInternal, setCompareModeInternal] = useState<CompareMode | undefined>(compareMode)
    const [compareRangeInternal, setCompareRangeInternal] = useState<DateRange | undefined>(compareRange)
    const [selectingCompareRange, setSelectingCompareRange] = useState<boolean>(false)

    const selectingCompareRangePrev = useRef<boolean>(false)
    const didApplyRef = useRef<boolean>(false)

    const rangeLatestRef = useRef<DateRange | undefined>(value ?? defaultValue ?? undefined)
    const compareEnabledLatestRef = useRef<boolean>(compare ?? false)
    const compareModeLatestRef = useRef<CompareMode | undefined>(compareMode)
    const compareRangeLatestRef = useRef<DateRange | undefined>(compareRange)

    const [startTime, setStartTime] = useState<TimeValue | null>(
        value?.from
            ? new Time(value.from.getHours(), value.from.getMinutes())
            : defaultValue?.from
                ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes())
                : new Time(0, 0),
    )
    const [endTime, setEndTime] = useState<TimeValue | null>(
        value?.to
            ? new Time(value.to.getHours(), value.to.getMinutes())
            : defaultValue?.to
                ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes())
                : new Time(0, 0),
    )

    const initialState = useMemo(() => {
        return {
            range,
            compareInternal,
            compareModeInternal,
            compareRangeInternal,
            selectingCompareRange,
        }
    }, [open])

    useEffect(() => {
        setRange(value ?? defaultValue ?? undefined)
        rangeLatestRef.current = value ?? defaultValue ?? undefined
    }, [value, defaultValue])

    useEffect(() => {
        setCompareInternal(compare ?? false)
        compareEnabledLatestRef.current = compare ?? false
    }, [compare])

    useEffect(() => {
        setCompareModeInternal(compareMode)
        compareModeLatestRef.current = compareMode
    }, [compareMode])

    useEffect(() => {
        setCompareRangeInternal(compareRange)
        compareRangeLatestRef.current = compareRange
    }, [compareRange])

    useEffect(() => {
        if (!compareInternal) {
            return
        }
        if (!compareModeInternal || compareModeInternal === "custom") {
            return
        }

        const computed = getCompareRange(compareModeInternal, range ?? { from: undefined, to: undefined })
        setCompareRangeInternal(computed)
        compareRangeLatestRef.current = computed
    }, [range?.from, range?.to, compareInternal, compareModeInternal])

    useEffect(() => {
        if (!open) {
            selectingCompareRangePrev.current = selectingCompareRange
            return
        }

        const enteredCompareSelection = !selectingCompareRangePrev.current && selectingCompareRange
        selectingCompareRangePrev.current = selectingCompareRange

        if (!enteredCompareSelection) {
            return
        }

        const baseFrom = compareRangeInternal?.from ?? range?.from
        if (!baseFrom) {
            return
        }

        setLeftMonth(monthStart(baseFrom))
    }, [open, selectingCompareRange, compareRangeInternal?.from, range?.from])

    useEffect(() => {
        if (!open) {
            return
        }

        if (leftMonth) {
            return
        }

        const baseFrom = selectingCompareRange
            ? compareRangeInternal?.from
            : range?.from

        if (!baseFrom) {
            return
        }

        setLeftMonth(monthStart(baseFrom))
    }, [open, selectingCompareRange, compareRangeInternal?.from, range?.from, leftMonth])

    useEffect(() => {
        if (!open) {
            if (range?.from) {
                setLeftMonth(monthStart(range.from))
            } else {
                setLeftMonth(undefined)
            }
        }
    }, [open])

    const onRangeChange = (newRange: DateRange | undefined, selectedDay?: Date) => {
        let finalRange = newRange

        // When both from and to are already set and the user clicks a new date
        // (not on an existing bound), reset to start a fresh selection instead
        // of adjusting bounds. This fixes the bug where the end date persists
        // from a previous selection when navigating to a different month.
        if (range?.from && range?.to && selectedDay) {
            const isClickOnExistingBound =
                isSameDay(selectedDay, range.from) ||
                isSameDay(selectedDay, range.to)

            if (!isClickOnExistingBound) {
                finalRange = { from: selectedDay, to: undefined }
            }
        }

        if (showTimePicker) {
            if (finalRange?.from && !startTime) {
                setStartTime(new Time(0, 0))
            }

            if (finalRange?.to && !endTime) {
                setEndTime(new Time(0, 0))
            }

            if (finalRange?.from && startTime) {
                finalRange.from.setHours(startTime.hour)
                finalRange.from.setMinutes(startTime.minute)
            }

            if (finalRange?.to && endTime) {
                finalRange.to.setHours(endTime.hour)
                finalRange.to.setMinutes(endTime.minute)
            }
        }

        setRange(finalRange)
        rangeLatestRef.current = finalRange
    }

    const onCompareRangeSelect = (dateRange: DateRange | undefined) => {
        setCompareRangeInternal(dateRange)
        compareRangeLatestRef.current = dateRange

        if (dateRange?.from && dateRange?.to) {
            setSelectingCompareRange(false)
        }
    }

    const onQuickSelect = (key: QuickSelectKey) => {
        const quickRange = getQuickSelectRange(key, new Date())
        onRangeChange(quickRange)

        const from = quickRange.from
        const to = quickRange.to

        if (!from) {
            return
        }

        setLeftMonth(monthStart(from))
    }

    const onToggleCompare = (checked: boolean) => {
        setCompareInternal(checked)
        compareEnabledLatestRef.current = checked

        if (!checked) {
            setCompareModeInternal(undefined)
            compareModeLatestRef.current = undefined
            setCompareRangeInternal(undefined)
            compareRangeLatestRef.current = undefined
            setSelectingCompareRange(false)
            return
        }

        setCompareModeInternal(undefined)
        compareModeLatestRef.current = undefined
        setCompareRangeInternal(undefined)
        compareRangeLatestRef.current = undefined
        setSelectingCompareRange(false)
    }

    const onSelectCompareMode = (mode: CompareMode) => {
        setCompareModeInternal(mode)
        compareModeLatestRef.current = mode

        if (mode === "custom") {
            setCompareRangeInternal(undefined)
            compareRangeLatestRef.current = undefined
            setSelectingCompareRange(true)
            return
        }

        setSelectingCompareRange(false)
        const computed = getCompareRange(mode, range ?? { from: undefined, to: undefined })
        setCompareRangeInternal(computed)
        compareRangeLatestRef.current = computed
    }

    const onCancel = () => {
        setRange(initialState.range)
        rangeLatestRef.current = initialState.range
        setCompareInternal(initialState.compareInternal)
        compareEnabledLatestRef.current = initialState.compareInternal
        setCompareModeInternal(initialState.compareModeInternal)
        compareModeLatestRef.current = initialState.compareModeInternal
        setCompareRangeInternal(initialState.compareRangeInternal)
        compareRangeLatestRef.current = initialState.compareRangeInternal
        setSelectingCompareRange(initialState.selectingCompareRange)
        setStartTime(
            initialState.range?.from
                ? new Time(initialState.range.from.getHours(), initialState.range.from.getMinutes())
                : new Time(0, 0),
        )
        setEndTime(
            initialState.range?.to
                ? new Time(initialState.range.to.getHours(), initialState.range.to.getMinutes())
                : new Time(0, 0),
        )
    }

    const onCancelClick = () => {
        didApplyRef.current = false
        onCancel()
        setOpen(false)
    }

    const onOpenChange = (open: boolean) => {
        if (!open) {
            if (didApplyRef.current) {
                didApplyRef.current = false
                setOpen(false)
                return
            }

            onCancel()
            setOpen(false)
            return
        }

        setOpen(true)
    }

    const onTimeChange = (time: TimeValue | null, pos: "start" | "end") => {
        switch (pos) {
            case "start":
                setStartTime(time)
                break
            case "end":
                setEndTime(time)
                break
        }

        if (!range) {
            return
        }

        if (pos === "start") {
            if (!range.from) {
                return
            }

            const newDate = new Date(range.from.getTime())

            if (!time) {
                newDate.setHours(0)
                newDate.setMinutes(0)
            } else {
                newDate.setHours(time.hour)
                newDate.setMinutes(time.minute)
            }

            setRange({
                ...range,
                from: newDate,
            })
        }

        if (pos === "end") {
            if (!range.to) {
                return
            }

            const newDate = new Date(range.to.getTime())

            if (!time) {
                newDate.setHours(0)
                newDate.setMinutes(0)
            } else {
                newDate.setHours(time.hour)
                newDate.setMinutes(time.minute)
            }

            setRange({
                ...range,
                to: newDate,
            })
        }
    }

    useEffect(() => {
        setRange(value ?? defaultValue ?? undefined)

        setStartTime(
            value?.from
                ? new Time(value.from.getHours(), value.from.getMinutes())
                : defaultValue?.from
                    ? new Time(
                        defaultValue.from.getHours(),
                        defaultValue.from.getMinutes(),
                    )
                    : new Time(0, 0),
        )
        setEndTime(
            value?.to
                ? new Time(value.to.getHours(), value.to.getMinutes())
                : defaultValue?.to
                    ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes())
                    : new Time(0, 0),
        )
    }, [value, defaultValue])

    const displayRange = useMemo(() => {
        if (!range) {
            return null
        }

        return `${range.from ? formatDate(range.from, locale, showTimePicker) : ""} - ${range.to ? formatDate(range.to, locale, showTimePicker) : ""
            }`
    }, [range, locale, showTimePicker])

    const displayCompareRange = useMemo(() => {
        if (!compareInternal || !compareRangeInternal?.from || !compareRangeInternal?.to) {
            return null
        }

        return `${formatDate(compareRangeInternal.from, locale, showTimePicker)} - ${formatDate(compareRangeInternal.to, locale, showTimePicker)}`
    }, [compareInternal, compareRangeInternal?.from, compareRangeInternal?.to, locale, showTimePicker])

    const triggerCompareLabel = useMemo(() => {
        if (!showCompare || !compareInternal) {
            return undefined
        }

        return "Compare"
    }, [showCompare, compareInternal, compareRangeInternal?.from, compareRangeInternal?.to, locale])

    const onApply = () => {
        didApplyRef.current = true
        const nextRange = rangeLatestRef.current
        const nextCompareEnabled = compareEnabledLatestRef.current
        const nextCompareMode = compareModeLatestRef.current
        let nextCompareRange = compareRangeLatestRef.current

        if (nextCompareEnabled && nextCompareMode && nextCompareMode !== "custom") {
            nextCompareRange = getCompareRange(nextCompareMode, nextRange ?? { from: undefined, to: undefined })
        }

        onChange?.(nextRange)

        onCompareChange?.(nextCompareEnabled)
        onCompareModeChange?.(nextCompareMode)
        onCompareRangeChange?.(nextCompareEnabled ? nextCompareRange : undefined)

        setOpen(false)
    }

    return (
        <PopoverPrimitives.Root
            tremor-id="tremor-raw"
            open={open}
            onOpenChange={onOpenChange}
        >
            <Trigger
                isCustom={!!trigger}
                placeholder={placeholder}
                disabled={disabled}
                className={className}
                hasError={hasError}
                compareLabel={triggerCompareLabel}
                aria-required={props.required || props["aria-required"]}
                aria-invalid={props["aria-invalid"]}
                aria-label={props["aria-label"]}
                aria-labelledby={props["aria-labelledby"]}
            >
                {trigger ?? displayRange}
            </Trigger>
            <CalendarPopover align={align}>
                <div className="flex pt-4 sm:min-h-[460px]">
                    <div className="flex flex-col overflow-x-auto sm:flex-row sm:items-stretch">
                        {(showQuickSelect || (presets && presets.length > 0)) && (
                            <div
                                className={cx(
                                    "relative flex h-16 w-full items-center sm:h-auto sm:w-56",
                                    "border-b border-gray-200 sm:border-r sm:border-b-0",
                                    "overflow-auto sm:overflow-visible",
                                )}
                            >
                                <div className="absolute px-3 sm:static sm:px-4 sm:py-4">
                                    {showQuickSelect ? (
                                        <div className="flex h-full flex-col">
                                            <div className="text-sm font-medium text-gray-900">Quick Select</div>
                                            <div className="mt-3">
                                                <ul className="flex items-start gap-x-1 sm:flex-col">
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Yesterday"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("yesterday")}
                                                            aria-label="Select Yesterday"
                                                        >
                                                            <span>Yesterday</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Last 7 Days"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("last_7_days")}
                                                            aria-label="Select Last 7 Days"
                                                        >
                                                            <span>Last 7 Days</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Last 14 Days"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("last_14_days")}
                                                            aria-label="Select Last 14 Days"
                                                        >
                                                            <span>Last 14 Days</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Last 30 Days"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("last_30_days")}
                                                            aria-label="Select Last 30 Days"
                                                        >
                                                            <span>Last 30 Days</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="This Month"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("this_month")}
                                                            aria-label="Select This Month"
                                                        >
                                                            <span>This Month</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Last Month"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("last_month")}
                                                            aria-label="Select Last Month"
                                                        >
                                                            <span>Last Month</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="This Year"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("this_year")}
                                                            aria-label="Select This Year"
                                                        >
                                                            <span>This Year</span>
                                                        </button>
                                                    </li>
                                                    <li className="sm:w-full sm:py-px">
                                                        <button
                                                            type="button"
                                                            title="Last Year"
                                                            className={cx(
                                                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                                                "text-gray-700",
                                                                "border-gray-200",
                                                                "focus-visible:bg-gray-100",
                                                                "hover:bg-gray-100",
                                                            )}
                                                            onClick={() => onQuickSelect("last_year")}
                                                            aria-label="Select Last Year"
                                                        >
                                                            <span>Last Year</span>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                            {showCompare && (
                                                <div className="mt-3 border-t border-gray-200 pt-3">
                                                    <Checkbox
                                                        label="Compare"
                                                        checked={compareInternal}
                                                        onChange={(e) => onToggleCompare(e.target.checked)}
                                                        labelTrigger
                                                    />
                                                    {compareInternal && (
                                                        <div className="mt-3">
                                                            <div className="text-xs text-gray-500">Compare to:</div>
                                                            <div className="mt-2 space-y-1">
                                                                {(showCompareOptions ? showCompareOptions.previous_month : true) && <button
                                                                    type="button"
                                                                    className={cx(
                                                                        "w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                                                        compareModeInternal === "previous_month"
                                                                            ? "bg-teal-600 text-white"
                                                                            : "text-gray-700 hover:bg-gray-100",
                                                                    )}
                                                                    onClick={() => onSelectCompareMode("previous_month")}
                                                                >
                                                                    Previous Month
                                                                </button>}
                                                                {(showCompareOptions ? showCompareOptions.previous_period : true) && <button
                                                                    type="button"
                                                                    className={cx(
                                                                        "w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                                                        compareModeInternal === "previous_period"
                                                                            ? "bg-teal-600 text-white"
                                                                            : "text-gray-700 hover:bg-gray-100",
                                                                    )}
                                                                    onClick={() => onSelectCompareMode("previous_period")}
                                                                >
                                                                    Previous Period
                                                                </button>}
                                                                {(showCompareOptions ? showCompareOptions.same_period_last_year : true) && <button
                                                                    type="button"
                                                                    className={cx(
                                                                        "w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                                                        compareModeInternal === "same_period_last_year"
                                                                            ? "bg-teal-600 text-white"
                                                                            : "text-gray-700 hover:bg-gray-100",
                                                                    )}
                                                                    onClick={() => onSelectCompareMode("same_period_last_year")}
                                                                >
                                                                    Same Period Last Year
                                                                </button>}
                                                                {(showCompareOptions ? showCompareOptions.same_period_last_month : true) && <button
                                                                    type="button"
                                                                    className={cx(
                                                                        "w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                                                        compareModeInternal === "same_period_last_month"
                                                                            ? "bg-teal-600 text-white"
                                                                            : "text-gray-700 hover:bg-gray-100",
                                                                    )}
                                                                    onClick={() => onSelectCompareMode("same_period_last_month")}
                                                                >
                                                                    Same Period Last Month
                                                                </button>}
                                                                {(showCompareOptions ? showCompareOptions.custom : true) && <button
                                                                    type="button"
                                                                    className={cx(
                                                                        "w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                                                        compareModeInternal === "custom"
                                                                            ? "bg-teal-600 text-white"
                                                                            : "text-gray-700 hover:bg-gray-100",
                                                                    )}
                                                                    onClick={() => onSelectCompareMode("custom")}
                                                                >
                                                                    Custom
                                                                </button>}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <PresetContainer
                                            currentValue={range}
                                            presets={presets ?? []}
                                            onSelect={onRangeChange}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col overflow-x-auto">
                            {selectingCompareRange && (
                                <div className="p-4 pb-0">
                                    <div className="rounded-md border border-teal-600 bg-teal-50 px-4 py-3 text-sm text-teal-700">
                                        Select custom comparison date range
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-row overflow-x-auto">
                                <CalendarPrimitive
                                    mode="range"
                                    selected={selectingCompareRange ? compareRangeInternal : range}
                                    onSelect={selectingCompareRange ? onCompareRangeSelect : onRangeChange}
                                    month={leftMonth}
                                    onMonthChange={setLeftMonth}
                                    numberOfMonths={1}
                                    disabled={disabledDays}
                                    disableNavigation={disableNavigation}
                                    enableYearNavigation={enableYearNavigation}
                                    locale={locale}
                                    initialFocus
                                    {...props}
                                />
                            </div>
                            {showTimePicker && (
                                <div className="flex items-center justify-evenly gap-x-3 border-t border-gray-200 p-3">
                                    <div className="flex flex-1 items-center gap-x-2">
                                        <span className="text-gray-700">
                                            {translations?.start ?? "Start"}:
                                        </span>
                                        <TimeInput
                                            value={startTime}
                                            onChange={(v) => onTimeChange(v, "start")}
                                            aria-label="Start date time"
                                            isDisabled={!range?.from}
                                            isRequired={props.required}
                                        />
                                    </div>
                                    <RiSubtractFill className="size-4 shrink-0 text-gray-400" />
                                    <div className="flex flex-1 items-center gap-x-2">
                                        <span className="text-gray-700">
                                            {translations?.end ?? "End"}:
                                        </span>
                                        <TimeInput
                                            value={endTime}
                                            onChange={(v) => onTimeChange(v, "end")}
                                            aria-label="End date time"
                                            isDisabled={!range?.to}
                                            isRequired={props.required}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="sm:mt-auto">
                                <div className="mx-3 border-y border-gray-200 px-3 py-4 text-center">
                                    <div className="text-sm text-gray-500">
                                        <span>
                                            Selected: <span className="font-medium text-gray-700">{displayRange}</span>
                                        </span>
                                    </div>
                                    {displayCompareRange && (
                                        <div className="mt-1 text-sm text-teal-700">
                                            Compare: <span className="font-medium">{displayCompareRange}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-end gap-x-2 px-3 py-3">
                                    <Button
                                        variant="light"
                                        className="h-8 w-full sm:w-fit"
                                        type="button"
                                        onClick={onCancelClick}
                                    >
                                        {translations?.cancel ?? "Cancel"}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="h-8 w-full sm:w-fit"
                                        type="button"
                                        onClick={onApply}
                                    >
                                        {translations?.apply ?? "Apply"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CalendarPopover>
        </PopoverPrimitives.Root>
    )
}

RangeDatePicker.displayName = "DatePicker.RangeDatePicker"

export { RangeDatePicker, type RangeProps }
